import { computed } from 'vue'
import { useRouter } from 'vue-router'
import type { RouteLocation } from 'vue-router'
/**
 * Custom composable function to manage router history.
 *
 * @returns {
 *    from: ComputedRef<RouteLocation | null>
 * }
 * An object containing the previous route from the router's history.
 *
 * @remarks
 * This function utilizes the Vue Router's `useRouter` composable to access the router instance.
 * It provides a method to retrieve the previous route from the router's history, if available.
 *
 * @example
 * ```typescript
 * import { useRouterHistory } from './useRouterHistory';
 *
 * const { from } = useRouterHistory();
 * console.log(from); // Logs the previous route if available, otherwise null
 * ```
 */
export const useRouterHistory = () => {
  const router = useRouter()

  // Function to get the previous route from the router's history
  const from = computed<RouteLocation | null>(() => {
    const history = router.options.history

    // Early return (null) if there is no valid previous route
    if (typeof history.state.back !== 'string') {
      return null
    }
    // Resolve the previous route from the router's history
    return router.resolve(history.state.back)
  })

  return {
    from
  }
}
